import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { 
  DetailedReportData, 
  ReportFilters as ReportFiltersType, 
  JewelerReportData, 
  MonthlyReportData,
  ReportResponse,
  MonthlyAnalysisResponse,
  ProductAnalysisResponse,
  UserReportResponse
} from '../utils/types';
import { 
  getDetailedReports, 
  getJewelerReports, 
  getMonthlyReports, 
  exportReportToExcel,
  getReports,
  getMonthlyAnalysis,
  getProductAnalysis,
  getUserReports
} from '../services/admin/AdminServices';
import { baseUrl, fetcher } from '../utils/global';
import useSWR from 'swr';
import DetailedReportCard from '../components/DetailedReportCard';
import ReportFilters from '../components/ReportFilters';
import MonthlyReportChart from '../components/MonthlyReportChart';
import JewelerReportTable from '../components/JewelerReportTable';
import ProductReport from '../components/ProductReport';
import ProductAnalysisChart from '../components/ProductAnalysisChart';
import { useAlert } from '../hooks/useAlert';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function DetailedAdminReportContainer() {
  const { showError } = useAlert();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [monthlyAnalysisData, setMonthlyAnalysisData] = useState<MonthlyAnalysisResponse | null>(null);
  const [productAnalysisData, setProductAnalysisData] = useState<ProductAnalysisResponse | null>(null);
  const [userReportData, setUserReportData] = useState<UserReportResponse | null>(null);
  const [filters, setFilters] = useState<ReportFiltersType>({
    reportType: 'monthly',
    period: 'monthly',
    periodOffset: 0,
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchReportData = async (currentFilters: ReportFiltersType) => {
    setLoading(true);
    try {
      const params: any = {};
      
      if (currentFilters.reportType === 'custom') {
        if (currentFilters.startDate) params.startDate = currentFilters.startDate;
        if (currentFilters.endDate) params.endDate = currentFilters.endDate;
      } else {
        params.period = currentFilters.period || currentFilters.reportType;
        params.periodOffset = currentFilters.periodOffset || 0;
      }

      // Fetch main report data (respect jewelerId/branchId if provided)
      const mainReportData = await getReports({ ...params, jewelerId: currentFilters.jewelerId, branchId: currentFilters.branchId });
      setReportData(mainReportData);

      // Fetch monthly analysis data
      const monthlyData = await getMonthlyAnalysis({ year: new Date().getFullYear(), jewelerId: currentFilters.jewelerId, branchId: currentFilters.branchId });
      setMonthlyAnalysisData(monthlyData);

      // Fetch product analysis data
      const productData = await getProductAnalysis({ ...params, jewelerId: currentFilters.jewelerId, branchId: currentFilters.branchId });
      setProductAnalysisData(productData);

      // Fetch user reports data (admin only)
      const userData = await getUserReports({ ...params, jewelerId: currentFilters.jewelerId, branchId: currentFilters.branchId });
      setUserReportData(userData);

    } catch (error) {
      showError('Rapor verileri yüklenirken hata oluştu');
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const handleFiltersChange = (newFilters: ReportFiltersType) => {
    setFilters(newFilters);
    fetchReportData(newFilters);
  };

  const handleExport = async (exportFilters: ReportFiltersType) => {
    try {
      await exportReportToExcel(exportFilters, 'detailed');
    } catch (error) {
      showError('Rapor export edilirken hata oluştu');
      console.error('Error exporting report:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getReportCards = () => {
    if (!reportData) return [];

    return [
      {
        title: 'Toplam Ciro',
        value: reportData.totalRevenue / 100,
        currency: 'TL',
        color: '#1976d2',
        trend: {
          value: 12.5,
          direction: 'up' as const
        }
      },
      {
        title: 'Toplam Kar',
        value: reportData.totalProfit / 100,
        currency: 'TL',
        color: '#2e7d32',
        trend: {
          value: 8.3,
          direction: 'up' as const
        }
      },
      {
        title: 'Net Kar',
        value: (reportData.totalProfit - reportData.totalLoss) / 100,
        currency: 'TL',
        color: reportData.totalProfit - reportData.totalLoss >= 0 ? '#2e7d32' : '#d32f2f',
        trend: {
          value: 15.2,
          direction: 'up' as const
        }
      }
    ];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '30px',
          fontWeight: '400',
          mb: 3
        }}
      >
        Detaylı Raporlar
      </Typography>

      <ReportFilters
        onFiltersChange={handleFiltersChange}
        onExport={handleExport}
        loading={loading}
        jewelers={[]}
      />

 

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="report tabs">
            <Tab label="Genel Bakış" />
            <Tab label="Aylık Analiz" />
            <Tab label="Kuyumcu Detayları" />
            <Tab label="Ürün Analizi" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {monthlyAnalysisData && (
                <MonthlyReportChart 
                  data={monthlyAnalysisData.monthlyData.map(item => ({
                    month: item.month.toString(),
                    year: item.year,
                    monthName: item.monthName,
                    revenue: item.totalRevenue,
                    profit: item.totalProfit,
                    loss: 0,
                    transactionCount: item.totalTransactions,
                    averageTransactionValue: item.averageTransactionValue,
                    jewelerBreakdown: []
                  }))} 
                  chartType="line"
                  showJewelerBreakdown={true}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Özet Bilgiler
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Toplam Kullanıcı:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {userReportData?.totalUsers || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Toplam İşlem:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData?.totalTransactions || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Ortalama İşlem Değeri:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData ? (reportData.averageTransactionValue / 100).toFixed(2) : 0} TL
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Kar Marjı:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData ? reportData.detailedMetrics.profitMargin.toFixed(2) : 0}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {monthlyAnalysisData && (
            <MonthlyReportChart 
              data={monthlyAnalysisData.monthlyData.map(item => ({
                month: item.month.toString(),
                year: item.year,
                monthName: item.monthName,
                revenue: item.totalRevenue,
                profit: item.totalProfit,
                loss: 0,
                transactionCount: item.totalTransactions,
                averageTransactionValue: item.averageTransactionValue,
                jewelerBreakdown: []
              }))} 
              chartType="bar"
              showJewelerBreakdown={true}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {userReportData && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Kullanıcı Raporları
              </Typography>
              <Grid container spacing={2}>
                {userReportData.userReports.map((userReport, index) => (
                  <Grid item xs={12} md={6} key={userReport.user.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {userReport.user.firstName} {userReport.user.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {userReport.user.email}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Ciro:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(userReport.metrics.totalRevenue / 100).toFixed(2)} TL
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Kar:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(userReport.metrics.totalProfit / 100).toFixed(2)} TL
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">İşlem Sayısı:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {userReport.metrics.totalTransactions}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ürün Analizi
            </Typography>
            {productAnalysisData && productAnalysisData.productAnalysis && productAnalysisData.productAnalysis.length > 0 ? (
              <Grid container spacing={2}>
                {productAnalysisData.productAnalysis.map((product, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Miktar:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {product.totalQuantity}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Ciro:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(product.totalRevenue / 100).toFixed(2)} TL
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Kar:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(product.totalProfit / 100).toFixed(2)} TL
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Kar Marjı:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {product.profitMargin.toFixed(2)}%
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              reportData && reportData.productSummary && Object.keys(reportData.productSummary).length > 0 ? (
                <Box>
                  <ProductReport products={reportData.productSummary} />
                  <Box sx={{ mt: 3 }}>
                    <ProductAnalysisChart 
                      products={reportData.productSummary} 
                      chartType="bar"
                      showTopProducts={10}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Veri bulunamadı.</Typography>
              )
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
}
